// src/components/OfflineBanner.jsx
//
// The one visible sign offline mode exists at all (this feature's own plan
// doc, "Real Offline Mode for the Cashier/Checkout Screen") — rendered once,
// app-wide (see App.js, right alongside AppToastContainer/FaviconSync),
// since a queued shift-close can finish syncing while the cashier has
// already navigated away from Checkout. Reads everything from
// OfflineContext — no props, no page-specific wiring needed.
//
// NOTE: uses useTranslation("common") with English fallbacks the same way
// ConfirmModal.jsx does — these particular keys aren't seeded into
// internal/config/seed_translations*.go yet (out of this feature's own
// approved scope), so every language currently shows the English fallback.
// Seeding real translations for these strings is a natural follow-up, not a
// functional gap: useTranslation's own fallback behavior (see
// I18nContext.js) means nothing breaks in the meantime.
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../context/I18nContext";
import { useOffline } from "../context/OfflineContext";

export default function OfflineBanner() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { isOnline, queueLength, syncStatus } = useOffline();

  // Nothing queued and connectivity is fine — the common case, zero visual
  // footprint.
  if (isOnline && queueLength === 0 && syncStatus !== "syncing") {
    return null;
  }

  let tone = "offline"; // "offline" | "syncing" | "complete" | "failed"
  let text;

  if (!isOnline) {
    tone = "offline";
    text = queueLength > 0
      ? t("offline_banner_offline_queued", `You're offline — ${queueLength} sale${queueLength === 1 ? "" : "s"} waiting to sync`)
      : t("offline_banner_offline", "You're offline — sales will be queued until you reconnect");
  } else if (syncStatus === "syncing") {
    tone = "syncing";
    text = t("offline_banner_syncing", "Back online — syncing queued sales…");
  } else if (syncStatus === "failed") {
    tone = "failed";
    text = t("offline_banner_failed", "Some queued sales need review");
  } else if (queueLength > 0) {
    tone = "syncing";
    text = t("offline_banner_queued", `${queueLength} sale${queueLength === 1 ? "" : "s"} still queued`);
  } else {
    tone = "complete";
    text = t("offline_banner_complete", "Sync complete");
  }

  const toneClasses = {
    offline: "bg-amber-500 text-white",
    syncing: "bg-blue-600 text-white",
    complete: "bg-emerald-600 text-white",
    failed: "bg-red-600 text-white",
  }[tone];

  const dotClasses = {
    offline: "bg-white/90",
    syncing: "bg-white/90 animate-pulse",
    complete: "bg-white/90",
    failed: "bg-white/90",
  }[tone];

  return (
    <div
      className={`fixed top-0 inset-x-0 z-[200] flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium shadow-md ${toneClasses}`}
      role="status"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${dotClasses}`} />
      {text}
      {tone === "failed" && (
        <button
          onClick={() => navigate("/checkout/offline-queue")}
          className="underline hover:no-underline font-bold"
        >
          {t("offline_banner_review_link", "Review")}
        </button>
      )}
    </div>
  );
}
